import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { PieChart, Pie, Cell } from 'recharts';
import {
  Sparkles,
  Clapperboard,
  Camera,
  CheckCheck,
  LayoutGrid,
  Settings,
  Brain,
  ChevronRight,
  FileUp,
  Lock,
  Calendar,
  Users,
  ArrowUpRight,
  FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders, useAuth } from '../context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { useFilm, useFilmScenes, useFilmMembers, useFilmEntitlements, useFilmCast, useFilmCrew, qk } from '../hooks/queries';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import ScriptImport from '../components/ScriptImport';
import { resolveAvatarUrl } from '../components/UserAvatar';
import { API_BASE } from '../config/api';
import { cn } from '../lib/utils';
import { usePdfExport } from '../hooks/usePdfExport';

const API = API_BASE;

/* ------------------------------ helpers ------------------------------ */

const deriveFilmStatus = (film) => {
  const sceneCount   = Number(film?.scene_count) || 0;
  const shotCount    = Number(film?.shot_count) || 0;
  const coveredShots = Number(film?.covered_shot_count) || 0;
  if (shotCount > 0 && coveredShots === shotCount) return 'COMPLETE';
  if (shotCount > 0 && coveredShots > 0)            return 'IN_PRODUCTION';
  if (sceneCount > 0)                               return 'PRE_PRODUCTION';
  return 'IN_REVIEW';
};

const STATUS_PILL = {
  IN_PRODUCTION:  { label: 'Production Status: Active',          tone: 'primary' },
  IN_REVIEW:      { label: 'Production Status: Active',          tone: 'primary' },
  PRE_PRODUCTION: { label: 'Production Status: Pre-Production',  tone: 'warning' },
  ON_HOLD:        { label: 'Production Status: On Hold',         tone: 'muted' },
  COMPLETE:       { label: 'Production Status: Complete',        tone: 'success' },
};

const TONE_CLASSES = {
  primary:     { text: 'text-primary',     bg: 'bg-primary/10',     ring: 'ring-primary/20' },
  warning:     { text: 'text-warning',     bg: 'bg-warning/10',     ring: 'ring-warning/20' },
  success:     { text: 'text-success',     bg: 'bg-success/10',     ring: 'ring-success/20' },
  destructive: { text: 'text-destructive', bg: 'bg-destructive/10', ring: 'ring-destructive/20' },
  muted:       { text: 'text-muted-foreground', bg: 'bg-muted',     ring: 'ring-border' },
};


const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - now) / 86400000);
};

const monthShort = (date) =>
  date.toLocaleString('en-US', { month: 'short' }).toUpperCase();

/* ------------------------------ subviews ------------------------------ */

const StatusPill = ({ status }) => {
  const meta = STATUS_PILL[status] || STATUS_PILL.IN_REVIEW;
  const tone = TONE_CLASSES[meta.tone] || TONE_CLASSES.primary;
  return (
    <div
      data-testid="fd-status-pill"
      className={cn(
        'inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-semibold tracking-[0.2em] uppercase',
        'border border-border bg-card ring-1 ring-inset',
        tone.text, tone.ring
      )}
    >
      <Sparkles className="size-3.5" />
      {meta.label}
    </div>
  );
};

const StatTile = ({ label, value, icon: Icon, testId, accent }) => (
  <div
    data-testid={testId}
    className="relative rounded-2xl border border-border bg-card p-6 min-h-[160px] overflow-hidden flex flex-col"
  >
    <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
      {label}
    </p>
    <div className="font-display text-5xl md:text-6xl font-bold tracking-tight mt-2 leading-none">
      {value}
    </div>
    {Icon && (
      <Icon
        aria-hidden
        className={cn('absolute right-4 bottom-4 size-16 pointer-events-none', accent || 'text-muted-foreground/15')}
      />
    )}
  </div>
);

const CoverageDonut = ({ shots, covered }) => {
  // Pull token-driven HSL at runtime so light/dark/dynamic themes work.
  // chart-1 is our primary orange token; muted is the empty ring.
  const [{ filledColor, emptyColor }, setColors] = useState({ filledColor: 'hsl(20 90% 56%)', emptyColor: 'hsl(220 10% 16%)' });

  useEffect(() => {
    const root = document.documentElement;
    const cs = getComputedStyle(root);
    const c1 = cs.getPropertyValue('--chart-1').trim();
    const m  = cs.getPropertyValue('--muted').trim();
    setColors({
      filledColor: c1 ? `hsl(${c1})` : 'hsl(20 90% 56%)',
      emptyColor:  m  ? `hsl(${m})`  : 'hsl(220 10% 16%)',
    });
  }, []);

  const safeShots = Math.max(0, Number(shots) || 0);
  const safeCovered = Math.max(0, Math.min(safeShots, Number(covered) || 0));
  const data = safeShots === 0
    ? [{ name: 'Empty', value: 1 }]
    : [
        { name: 'Covered',  value: safeCovered },
        { name: 'Uncovered', value: Math.max(0, safeShots - safeCovered) },
      ];

  return (
    <div className="relative size-24 md:size-28 shrink-0" data-testid="fd-coverage-donut">
      <PieChart width={112} height={112}>
        <Pie
          data={data}
          dataKey="value"
          innerRadius="65%"
          outerRadius="100%"
          startAngle={90}
          endAngle={-270}
          stroke="none"
          isAnimationActive={false}
        >
          {data.map((entry, i) => (
            <Cell
              key={i}
              fill={
                safeShots === 0
                  ? emptyColor
                  : entry.name === 'Covered' ? filledColor : emptyColor
              }
            />
          ))}
        </Pie>
      </PieChart>
      <span className="absolute inset-0 flex items-center justify-center">
        <CheckCheck className={cn('size-6', safeCovered > 0 ? 'text-primary' : 'text-muted-foreground/50')} />
      </span>
    </div>
  );
};

const CoverageStatTile = ({ shots, covered }) => {
  const pct = shots > 0 ? Math.round((covered / shots) * 100) : 0;
  return (
    <div
      data-testid="fd-stat-coverage"
      className="rounded-2xl border border-border bg-card p-6 min-h-[160px] flex items-center gap-4"
    >
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
          Covered Shots
        </p>
        <div className="mt-2 flex items-baseline gap-1">
          <span className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-none">
            {pct}
          </span>
          <span className="text-2xl md:text-3xl font-display text-muted-foreground font-semibold">%</span>
        </div>
        <p className="mt-2 text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
          {covered} / {shots} covered
        </p>
      </div>
      <CoverageDonut shots={shots} covered={covered} />
    </div>
  );
};

/* ------------------------------ Quick Actions ------------------------------ */

const QuickActionRow = ({ icon: Icon, label, sub, accent, onClick, locked, lockedHint, badge, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group w-full flex items-center gap-4 rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:bg-accent/40 transition-colors text-left"
  >
    <div className={cn('relative size-12 rounded-lg flex items-center justify-center shrink-0', accent.bg)}>
      <Icon className={cn('size-5', accent.text)} />
      {locked && (
        <span className="absolute -top-1 -right-1 size-5 rounded-full bg-card border border-border flex items-center justify-center">
          <Lock className="size-3 text-muted-foreground" />
        </span>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="font-semibold text-foreground truncate">{label}</p>
        {badge && (
          <span className="text-[9px] font-bold tracking-[0.18em] uppercase text-warning bg-warning/10 px-1.5 py-0.5 rounded">
            {badge}
          </span>
        )}
      </div>
      <p className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground mt-0.5 truncate">
        {locked ? (lockedHint || 'Pro feature — upgrade to unlock') : sub}
      </p>
    </div>
    <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
  </button>
);

/* ------------------------------ Production Team (Cast + Crew) ------------------------------ */
// Read-only summary cards backed by GET /api/films/{film_id}/cast and /crew.
// Managing cast/crew remains FilmManagement's job — the footer CTAs deep-link
// to FilmManagement?tab=castcrew so the user lands directly on the right tab.

/** initials("Priya Sharma") -> "PS"; falls back to the first two letters of the input. */
const initialsOf = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** One row per member. Mirrors FilmManagement's text treatment (name + role
 *  sub-line) but adds an initials avatar so the dashboard reads at a glance. */
const MemberRow = ({ member }) => (
  <div className="flex items-center gap-3 py-2" data-testid={`fd-member-${member.id}`}>
    <span
      className="shrink-0 size-9 rounded-full bg-secondary text-foreground/80 flex items-center justify-center text-xs font-semibold"
      aria-hidden
    >
      {initialsOf(member.name)}
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium leading-tight truncate">{member.name}</p>
      {member.role && (
        <p className="text-xs text-muted-foreground truncate">{member.role}</p>
      )}
    </div>
  </div>
);

const MemberRowSkeleton = () => (
  <div className="flex items-center gap-3 py-2">
    <span className="shrink-0 size-9 rounded-full bg-muted animate-pulse" />
    <div className="flex-1 space-y-1.5">
      <div className="h-3 bg-muted rounded w-1/2 animate-pulse" />
      <div className="h-2.5 bg-muted/60 rounded w-1/3 animate-pulse" />
    </div>
  </div>
);

/**
 * MemberSummaryCard — shared shell for Cast & Crew.
 *
 * Behaviour:
 *   • loading → 3 row skeletons.
 *   • error → inline retry button (each card fails independently).
 *   • populated → top 3 members + "+N more" chip if >3.
 *   • empty → friendly icon + copy + "+ Add {label}" link (editors only).
 *
 * The footer "Manage {label} →" CTA is gated by `canManage` — viewers don't
 * see a destination they can't act on.
 */
const MemberSummaryCard = ({
  label,            // 'cast' | 'crew' (lowercase, used in copy + testid)
  members,
  loading,
  error,
  onRetry,
  canManage,
  manageHref,
  addHref,
}) => {
  const Heading = label === 'cast' ? 'Cast' : 'Crew';
  const top = (members || []).slice(0, 3);
  const overflow = Math.max(0, (members?.length || 0) - top.length);

  return (
    <div
      className="rounded-2xl border border-border bg-card flex flex-col"
      data-testid={`fd-${label}-card`}
    >
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="size-7 rounded-lg bg-secondary flex items-center justify-center">
            <Users className="size-3.5 text-primary" />
          </span>
          <h3 className="font-display text-base font-semibold tracking-tight">{Heading}</h3>
          {!loading && !error && (
            <span
              className="text-[11px] font-semibold tracking-wider text-muted-foreground"
              data-testid={`fd-${label}-count`}
            >
              {(members?.length || 0)}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 px-5 py-3 min-h-[180px]">
        {loading && (
          <div className="space-y-1.5">
            <MemberRowSkeleton />
            <MemberRowSkeleton />
            <MemberRowSkeleton />
          </div>
        )}

        {!loading && error && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-2 py-4">
            <p className="text-sm text-destructive">Couldn't load {label}.</p>
            <Button variant="outline" size="sm" onClick={onRetry} data-testid={`fd-${label}-retry`}>
              Try again
            </Button>
          </div>
        )}

        {!loading && !error && (members?.length || 0) === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-2 py-4">
            <span className="size-10 rounded-xl bg-secondary flex items-center justify-center">
              <Users className="size-4 text-muted-foreground" />
            </span>
            <p className="text-sm font-medium">No {label} added yet</p>
            {canManage && (
              <Link
                to={addHref}
                className="text-xs font-semibold tracking-wider text-primary hover:underline"
                data-testid={`fd-${label}-empty-add`}
              >
                + Add {label}
              </Link>
            )}
          </div>
        )}

        {!loading && !error && (members?.length || 0) > 0 && (
          <div className="divide-y divide-border/60">
            {top.map((m) => <MemberRow key={m.id} member={m} />)}
            {overflow > 0 && (
              <p className="pt-2 text-[11px] font-semibold tracking-wider text-muted-foreground" data-testid={`fd-${label}-more`}>
                +{overflow} more
              </p>
            )}
          </div>
        )}
      </div>

      {!loading && !error && canManage && (members?.length || 0) > 0 && (
        <div className="px-5 pb-4">
          <Link
            to={manageHref}
            className="inline-flex items-center text-xs font-semibold tracking-wider text-primary hover:underline"
            data-testid={`fd-${label}-manage`}
          >
            Manage {label} →
          </Link>
        </div>
      )}
    </div>
  );
};

/* ------------------------------ Deadlines + Team ------------------------------ */
// MOCKED-DERIVED: deadlines below are read from real `due_date` columns on
// scenes (and shots when the column ships). Empty state when no rows match.

const DeadlinesCard = ({ scenes, film }) => {
  const calendarPath = film?.workspace_id
    ? `/workspaces/${film.workspace_id}/films/${film.id}/calendar`
    : '/calendar';
  const items = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const within30 = new Date();
    within30.setHours(0, 0, 0, 0);
    within30.setDate(within30.getDate() + 30);

    return (scenes || [])
      .filter((s) => s.due_date)
      .map((s) => {
        const due = new Date(s.due_date);
        return { scene: s, due, days: daysUntil(s.due_date) };
      })
      .filter((x) => x.due >= today && x.due <= within30)
      .sort((a, b) => a.due - b.due)
      .slice(0, 4);
  }, [scenes]);

  return (
    <div
      className="rounded-2xl border border-border bg-card p-6"
      data-testid="fd-deadlines"
    >
      <div className="flex items-center justify-between mb-5">
        <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-muted-foreground">
          Upcoming Deadlines
        </p>
        <Link
          to={calendarPath}
          className="text-[11px] font-semibold tracking-[0.18em] uppercase text-primary hover:text-primary/80 inline-flex items-center gap-1"
        >
          View Calendar <ArrowUpRight className="size-3" />
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-6">
          <Calendar className="size-7 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-foreground">No deadlines tracked yet.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Add due dates to scenes to populate this view.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map(({ scene, due, days }) => {
            const chip = days <= 2 ? { tone: 'destructive', label: days <= 0 ? 'TODAY' : `IN ${days}D` }
                       : days <= 7 ? { tone: 'warning',     label: `IN ${days}D` }
                                   : { tone: 'primary',     label: `IN ${days}D` };
            const chipTone = TONE_CLASSES[chip.tone];
            return (
              <li
                key={scene.id}
                data-testid={`fd-deadline-${scene.id}`}
                className="flex items-center gap-3 rounded-xl border border-border bg-card/50 px-3 py-2"
              >
                <div className="size-12 rounded-lg bg-muted text-foreground text-center flex flex-col items-center justify-center shrink-0">
                  <span className="text-[9px] tracking-widest text-muted-foreground leading-none mt-0.5">
                    {monthShort(due)}
                  </span>
                  <span className="font-display text-lg font-bold leading-none mt-0.5">
                    {due.getDate()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">
                    {scene.scene_name || `Scene ${scene.scene_number}`}
                  </p>
                  <p className="text-[10px] tracking-[0.16em] uppercase text-muted-foreground truncate">
                    {(film?.title || 'Untitled').toUpperCase()} · Scene {scene.scene_number}
                  </p>
                </div>
                <span className={cn(
                  'shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-[0.18em] uppercase',
                  chipTone.bg, chipTone.text
                )}>
                  {chip.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const TeamCard = ({ members, currentUserId, manageHref }) => {
  const total = members?.length || 0;
  const visible = (members || []).slice(0, 8);
  const more = Math.max(0, total - visible.length);

  const counts = useMemo(() => {
    const c = { owner: 0, admin: 0, editor: 0, viewer: 0 };
    for (const m of members || []) {
      if (c[m.access_type] != null) c[m.access_type] += 1;
    }
    return c;
  }, [members]);

  const summary = [];
  if (counts.owner)  summary.push(`${counts.owner} OWNER`);
  if (counts.admin)  summary.push(`${counts.admin} ADMIN${counts.admin > 1 ? 'S' : ''}`);
  if (counts.editor) summary.push(`${counts.editor} EDITOR${counts.editor > 1 ? 'S' : ''}`);
  if (counts.viewer) summary.push(`${counts.viewer} VIEWER${counts.viewer > 1 ? 'S' : ''}`);

  return (
    <div className="rounded-2xl border border-border bg-card p-6 flex flex-col" data-testid="fd-team">
      <div className="flex items-center justify-between mb-5">
        <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-muted-foreground">
          Team
        </p>
        <Link
          to={manageHref}
          className="text-[11px] font-semibold tracking-[0.18em] uppercase text-primary hover:text-primary/80 inline-flex items-center gap-1"
        >
          Manage <ArrowUpRight className="size-3" />
        </Link>
      </div>
      {total === 0 ? (
        <div className="text-center py-6">
          <Users className="size-7 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-foreground">No team yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Invite people from Manage Film.</p>
        </div>
      ) : (
        <>
          <div className="flex -space-x-2 mb-4">
            {visible.map((m) => {
              const initial = (m.full_name || m.email || '?').charAt(0).toUpperCase();
              const isOwner = m.access_type === 'owner' || m.user_id === currentUserId;
              return (
                <div
                  key={m.user_id || m.id}
                  title={m.full_name || m.email}
                  className={cn(
                    'size-10 rounded-full border-2 border-card flex items-center justify-center font-semibold text-sm',
                    isOwner ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                  )}
                >
                  {m.avatar_url ? (
                    <img
                      src={resolveAvatarUrl(m.avatar_url)}
                      alt=""
                      className="size-full rounded-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : initial}
                </div>
              );
            })}
            {more > 0 && (
              <div className="size-10 rounded-full border-2 border-card bg-muted text-muted-foreground text-xs font-semibold flex items-center justify-center">
                +{more}
              </div>
            )}
          </div>
          <p className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
            {summary.join(' · ')}
          </p>
        </>
      )}
    </div>
  );
};

/* ============================== Page ============================== */

const FilmDashboard = () => {
  const { filmId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Script-first onboarding (Phase 1 Task 2): `?import=script` set by the
  // create-film flow auto-expands + scrolls to the script-upload dropzone.
  const [searchParams] = useSearchParams();
  const autoImportScript = searchParams.get('import') === 'script';

  /* ----- data fetching (TanStack Query — [I]) ----- */
  const filmQuery = useFilm(filmId);
  const scenesQuery = useFilmScenes(filmId);
  const membersQuery = useFilmMembers(filmId);
  const entitlementsQuery = useFilmEntitlements(filmId);
  const castQuery = useFilmCast(filmId);
  const crewQuery = useFilmCrew(filmId);

  const film = filmQuery.data ?? null;
  const scenes = useMemo(() => scenesQuery.data || [], [scenesQuery.data]);
  const members = useMemo(() => membersQuery.data || [], [membersQuery.data]);
  const entitlements = entitlementsQuery.data ?? null;
  const loading =
    filmQuery.isLoading || scenesQuery.isLoading || membersQuery.isLoading || entitlementsQuery.isLoading;

  // Cast + Crew load independently (each card can retry without re-pulling the
  // rest). RQ exposes loading/error per query.
  const cast = useMemo(() => castQuery.data || [], [castQuery.data]);
  const crew = useMemo(() => crewQuery.data || [], [crewQuery.data]);
  const castState = { loading: castQuery.isLoading, error: castQuery.isError };
  const crewState = { loading: crewQuery.isLoading, error: crewQuery.isError };

  useEffect(() => {
    if (filmQuery.isError) toast.error('Failed to load film');
  }, [filmQuery.isError]);

  // Script-first onboarding: once the page has data, bring the script-upload
  // dropzone into view so a fresh film starts at the upload step.
  useEffect(() => {
    if (!autoImportScript || loading) return;
    const t = setTimeout(() => {
      const el = document.getElementById('fd-script-import');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    return () => clearTimeout(t);
  }, [autoImportScript, loading]);

  // Refetch the film bundle (e.g. after a script import adds scenes). RQ's
  // refetchOnWindowFocus (honouring staleTime) replaces the old manual
  // visibilitychange listener — no tab-focus refetch storms.
  const refetchFilmBundle = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: qk.film(filmId) });
    queryClient.invalidateQueries({ queryKey: qk.filmScenes(filmId) });
    queryClient.invalidateQueries({ queryKey: qk.filmMembers(filmId) });
    queryClient.invalidateQueries({ queryKey: qk.filmEntitlements(filmId) });
  }, [queryClient, filmId]);

  /* ----- derivations ----- */

  // Prefer permission fields baked into the film response (single source of
  // truth from the backend). Fall back to legacy owner+member derivation
  // for older payloads.
  const isOwner =
    typeof film?.is_owner === 'boolean'
      ? film.is_owner
      : !!film && film.user_id === user?.id;
  const myMember  = useMemo(() => members.find((m) => m.user_id === user?.id), [members, user?.id]);
  const legacyEditAccess = isOwner || (myMember && ['admin', 'editor'].includes(myMember.access_type));
  const canManage =
    typeof film?.can_edit === 'boolean'
      ? film.can_edit
      : legacyEditAccess;

  const canUploadScript =
    entitlements?.entitlements?.can_upload_script?.allowed === true;

  // Per-scene AI re-analysis (the AI Script Analysis page) stays Pro/Studio-
  // only even though script upload is now available on Free (Phase 1 Task 3).
  // Prefer the explicit backend entitlement; fall back to deriving the tier
  // from free_ai_scene_cap (null = paid plan) for older payloads.
  const canReanalyzeAI = entitlements?.entitlements?.can_use_ai_reanalysis
    ? entitlements.entitlements.can_use_ai_reanalysis.allowed === true
    : (canUploadScript && entitlements?.free_ai_scene_cap == null);

  // Every plan can export; Free copies carry the FrameFlow footer (M0.3).
  const exportWatermarked = entitlements?.export_watermarked === true;
  const { exportPdf, exporting: exportingPdf } = usePdfExport();

  const filmStatus    = deriveFilmStatus(film);
  const workspaceId   = film?.workspace_id;
  const baseFilmPath  = workspaceId ? `/workspaces/${workspaceId}/films/${filmId}` : `/films/${filmId}`;
  const boardHref     = `${baseFilmPath}/board`;
  const manageHref    = `${baseFilmPath}/manage`;
  const calendarHref  = `${baseFilmPath}/calendar`;

  /* ----- loading + not-found ----- */

  if (loading && !film) {
    return (
      <div className="container mx-auto px-4 md:px-6 py-10 md:py-14" data-testid="film-dashboard-loading">
        <Skeleton className="h-4 w-40 mb-3" />
        <Skeleton className="h-12 md:h-16 w-2/3 mb-3" />
        <Skeleton className="h-4 w-1/2 mb-10" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Skeleton className="h-[160px] rounded-2xl" />
          <Skeleton className="h-[160px] rounded-2xl" />
          <Skeleton className="h-[160px] rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!film) {
    return (
      <div className="container mx-auto px-4 md:px-6 py-20 text-center">
        <p className="text-destructive">Film not found</p>
      </div>
    );
  }

  const sceneCount  = Number(film.scene_count) || 0;
  const shotCount   = Number(film.shot_count) || 0;
  const coveredCount = Number(film.covered_shot_count) || 0;

  return (
    <div className="bg-background" data-testid="film-dashboard">
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Hero band */}
        <section className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6 mb-8">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-primary mb-3">
              Current Project
            </p>
            <h1
              data-testid="fd-hero-title"
              className="font-display font-bold tracking-tight text-4xl sm:text-5xl md:text-6xl leading-[1.05] truncate"
            >
              {film.title}
            </h1>
            {film.description && (
              <p className="text-base text-muted-foreground max-w-xl mt-3 line-clamp-2">
                {film.description}
              </p>
            )}
          </div>
          <div className="shrink-0">
            <StatusPill status={filmStatus} />
          </div>
        </section>

        {/* 3 stat tiles */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="fd-stats">
          <StatTile
            label="Total Scenes"
            value={sceneCount}
            icon={Clapperboard}
            testId="fd-stat-scenes"
          />
          <StatTile
            label="Total Shots"
            value={shotCount}
            icon={Camera}
            testId="fd-stat-shots"
          />
          <CoverageStatTile shots={shotCount} covered={coveredCount} />
        </section>

        {/* Quick Actions + Import Script */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          {/* Quick Actions */}
          <div data-testid="fd-quick-actions">
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-3">
              Quick Actions
            </p>
            <div className="space-y-3">
              <QuickActionRow
                testId="fd-action-scene-board"
                icon={LayoutGrid}
                label="Open Scene Board"
                sub="Manage scenes & shots"
                accent={{ bg: 'bg-primary/15', text: 'text-primary' }}
                onClick={() => navigate(boardHref)}
              />
              <QuickActionRow
                testId="fd-action-calendar"
                icon={Calendar}
                label="Production Calendar"
                sub="Schedule shoot days & deadlines"
                accent={{ bg: 'bg-blue-500/15', text: 'text-blue-500' }}
                onClick={() => navigate(calendarHref)}
              />
              <QuickActionRow
                testId="fd-action-export-pdf"
                icon={FileDown}
                label={exportingPdf ? 'Preparing PDF…' : 'Export Shot List (PDF)'}
                sub={exportWatermarked ? 'Print-ready · FrameFlow footer on Free' : 'Print-ready · scenes, shots & coverage'}
                accent={{ bg: 'bg-emerald-500/15', text: 'text-emerald-500' }}
                onClick={() => exportPdf(`/films/${filmId}/export/shot-list.pdf`, {
                  kind: 'shot_list',
                  fallbackName: `FrameFlow — ${film?.title || 'Film'} — Shot list.pdf`,
                })}
              />
              {canManage && (
                <QuickActionRow
                  testId="fd-action-manage"
                  icon={Settings}
                  label="Manage Film"
                  sub="Edit details, members, and access"
                  accent={{ bg: 'bg-warning/15', text: 'text-warning' }}
                  onClick={() => navigate(manageHref)}
                />
              )}
              <QuickActionRow
                testId="fd-action-ai"
                icon={Brain}
                label="AI Script Analysis"
                sub="Characters, props, shots & breakdowns"
                accent={{ bg: 'bg-chart-4/20', text: 'text-chart-4' }}
                badge="PRO"
                locked={!canReanalyzeAI}
                lockedHint="Upgrade to Pro to unlock"
                onClick={() => {
                  if (!canReanalyzeAI) {
                    navigate('/billing');
                    return;
                  }
                  // Navigate to the dedicated AI Breakdown page
                  const href = workspaceId
                    ? `/workspaces/${workspaceId}/films/${filmId}/ai-breakdown`
                    : `/films/${filmId}/ai-breakdown`;
                  navigate(href);
                }}
              />
            </div>
          </div>

          {/* Import Script */}
          <div id="fd-script-import" data-testid="fd-script-import-section">
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-3">
              Import Script
            </p>
            {canUploadScript ? (
              <div className="rounded-2xl border border-dashed border-border bg-card/40 p-1 hover:border-primary/40 transition-colors">
                {/* Wrap existing ScriptImport — preserves all behavior, restyles with our outer dropzone shell. */}
                <div className="rounded-2xl">
                  <div className="flex flex-col items-center justify-center text-center px-4 pt-6 pb-2">
                    <span className="size-12 rounded-xl bg-card flex items-center justify-center mb-3 ring-1 ring-border">
                      <FileUp className="size-5 text-primary" />
                    </span>
                    <p className="font-display text-xl font-semibold tracking-tight">
                      Drop your Final Draft or PDF here
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Up to 10MB. AI analysis starts automatically.
                    </p>
                  </div>
                  <ScriptImport
                    filmId={filmId}
                    onImportComplete={refetchFilmBundle}
                    freeSceneCap={entitlements?.free_ai_scene_cap ?? null}
                    autoExpand={autoImportScript}
                  />
                </div>
              </div>
            ) : (
              <div
                className="rounded-2xl border border-dashed border-border bg-card/40 p-8 text-center min-h-[220px] flex flex-col items-center justify-center"
                data-testid="fd-script-locked"
              >
                <span className="size-12 rounded-xl bg-secondary flex items-center justify-center mb-3">
                  <Lock className="size-5 text-muted-foreground" />
                </span>
                <p className="font-display text-xl font-semibold tracking-tight">
                  Script Import is a Pro Feature
                </p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                  Drop a Final Draft or PDF and let FrameFlow auto-build the
                  scene & shot list. Available on Pro and Studio plans.
                </p>
                <Button asChild size="sm" className="rounded-xl mt-4">
                  <Link to="/billing">Upgrade to Pro</Link>
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Production Team — Cast + Crew snapshot. Read-only summary, full
            management lives in FilmManagement (deep-linked via ?tab=castcrew). */}
        <section className="mb-12" data-testid="fd-production-team">
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-muted-foreground mb-3">
            Production Team
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <MemberSummaryCard
              label="cast"
              members={cast}
              loading={castState.loading}
              error={castState.error}
              onRetry={() => castQuery.refetch()}
              canManage={canManage}
              manageHref={`${manageHref}?tab=castcrew`}
              addHref={`${manageHref}?tab=castcrew`}
            />
            <MemberSummaryCard
              label="crew"
              members={crew}
              loading={crewState.loading}
              error={crewState.error}
              onRetry={() => crewQuery.refetch()}
              canManage={canManage}
              manageHref={`${manageHref}?tab=castcrew`}
              addHref={`${manageHref}?tab=castcrew`}
            />
          </div>
        </section>

        {/* Pro widgets row: Deadlines + Team */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <DeadlinesCard scenes={scenes} film={film} />
          </div>
          <div className="lg:col-span-1">
            <TeamCard
              members={members}
              currentUserId={user?.id}
              manageHref={canManage ? manageHref : `/workspaces/${workspaceId}/members`}
            />
          </div>
        </section>
      </main>
    </div>
  );
};

export default FilmDashboard;
